<template>
  <div class="hublot-container">
    <div class="scrolling-code-layer">
      <div v-for="(snippet, index) in activeSnippets" :key="index" class="code-line" :style="snippet.style" v-html="snippet.html"></div>
    </div>
    <div class="blur-overlay"></div>

    <div class="hublot-content">
      <h1 v-if="!isInputFocused && !userCommand" @click="focusInput" class="title-display" v-html="t('TheHublot.title')"></h1>
      
      <div v-else class="command-interface">
        <div class="last-command-wrapper">
          <transition name="fade">
            <div v-if="lastExecutedCommand" class="last-command-display">
              <span class="prompt-mini">{{ t('TheHublot.last_execution') }}</span> 
              <span class="cmd-history">{{ lastExecutedCommand }}</span>
            </div>
          </transition>
        </div>

        <div class="input-line">
          <span class="prompt">></span>
          <input ref="commandInput" v-model="userCommand" @keyup.enter="executeCommand" @keydown.up.prevent="handleHistoryNav('up')" @keydown.down.prevent="handleHistoryNav('down')" @blur="handleBlur" type="text" class="cmd-input" autocomplete="off" spellcheck="false" :placeholder="t('TheHublot.placeholder')" />
        </div>
      </div>
      
      <p class="subtitle" :class="{ 'feedback-active': feedbackMessage }">
        {{ feedbackMessage || t('TheHublot.subtitle') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '../composables/useTheme' 
import { useProjectStore } from '../store/index' 

const { t } = useI18n()
const { switchTheme } = useTheme()
const projectStore = useProjectStore()

// ... (Garde tes données snippetsSource et keywordsSource, elles n'ont pas besoin d'être traduites car c'est du code) ...
const snippetsSource = ["const name = \"Quentin\";", "function greet() { return \"Hello\"; }", "let age = 23;", "console.log(\"Hello, World!\");", "const sum = (a, b) => a + b;", "if (true) { console.log(\"It works!\"); }", "for (let i = 0; i < 10; i++) { console.log(i); }", "while (true) { console.log(\"Looping...\"); }", "try { throw new Error(\"Oops!\"); } catch (e) { console.error(e); }", "import { ref } from 'vue';", "export default defineComponent({});", "public static void main(String[] args)", "return new ResponseEntity<>(response, HttpStatus.OK);", "@Override public void run()", "SELECT * FROM users WHERE id = 1;", "git commit -m \"Refactor code\"", "docker-compose up -d"]
const keywordsSource = [{ word: "public", class: "keyword-blue" }, { word: "class", class: "keyword-blue" }, { word: "return", class: "keyword-orange" }, { word: "function", class: "keyword-blue" }, { word: "const", class: "keyword-orange" }, { word: "let", class: "keyword-red" }, { word: "var", class: "keyword-orange" }, { word: "if", class: "keyword-orange" }, { word: "else", class: "keyword-orange" }, { word: "for", class: "keyword-orange" }, { word: "while", class: "keyword-orange" }, { word: "import", class: "keyword-green" }, { word: "export", class: "keyword-green" }, { word: "from", class: "keyword-green" }, { word: "true", class: "keyword-green" }, { word: "false", class: "keyword-green" }, { word: "null", class: "keyword-green" }, { word: "console", class: "keyword-green" }, { word: "log", class: "keyword-green" }, { word: "new", class: "keyword-blue" }, { word: "this", class: "keyword-blue" }, { word: "try", class: "keyword-orange" }, { word: "catch", class: "keyword-orange" }, { word: "async", class: "keyword-blue" }, { word: "await", class: "keyword-blue" }]

const activeSnippets = ref([])
// ... (Garde colorize et generateSnippets identiques) ...
const colorize = (text) => { let html = text; keywordsSource.forEach(({ word, class: cssClass }) => { const regex = new RegExp(`\\b${word}\\b`, 'g'); html = html.replace(regex, `<span class="${cssClass}">${word}</span>`) }); html = html.replace(/(["'])(?:(?=(\\?))\2.)*?\1/g, '<span class="keyword-green">$&</span>'); return html }
const generateSnippets = () => { activeSnippets.value = Array.from({ length: 35 }).map(() => { const text = snippetsSource[Math.floor(Math.random() * snippetsSource.length)]; const duration = Math.random() * 20 + 20 + 's'; const top = Math.random() * 90 + '%'; const fontSize = (Math.random() * (0.8 - 0.4) + 0.4) + 'em'; return { html: colorize(text), style: { animationDuration: duration, top: top, fontSize: fontSize, } } }) }

const userCommand = ref('')
const isInputFocused = ref(false)
const commandInput = ref(null)
const feedbackMessage = ref('')
const lastExecutedCommand = ref('') 
const commandHistory = ref([])
const historyIndex = ref(-1)

const focusInput = async () => { isInputFocused.value = true; await nextTick(); commandInput.value?.focus() }
const handleBlur = () => { if (userCommand.value === '') { isInputFocused.value = false; feedbackMessage.value = '' } }

const executeCommand = () => {
  const cmd = userCommand.value.toLowerCase().trim()
  if (!cmd) return

  commandHistory.value.push(cmd)
  historyIndex.value = -1 
  lastExecutedCommand.value = cmd

  if (cmd === 'help') {
    feedbackMessage.value = t('TheHublot.cmd_help')
  } 
 else if (cmd === 'update' || cmd === 'fetch') {
   const shouldForce = cmd === 'update'
   
   projectStore.fetchProjects(shouldForce)
   feedbackMessage.value = t('TheHublot.cmd_update')
}
  else if (cmd.includes('style')) {
    if (cmd.includes('y2k')) switchTheme('y2k')
    else if (cmd.includes('dark')) switchTheme('dark')
    else if (cmd.includes('reset')) switchTheme('default')
    feedbackMessage.value = t('TheHublot.cmd_style')
  } 
  else if(cmd === 'cd'){
    feedbackMessage.value = t('TheHublot.cmd_cd')
  }
  else if (cmd === 'snake') {
    feedbackMessage.value = t('TheHublot.cmd_snake')
  } 
  else if (cmd === 'ls') {
    feedbackMessage.value = t('TheHublot.cmd_ls')
  } 
  else if (cmd === 'whoami') {
    feedbackMessage.value = t('TheHublot.cmd_whoami')
  } 
  else if (cmd === 'rm -rf' || cmd === 'rm -rf /') {
    feedbackMessage.value = t('TheHublot.cmd_rm')
    
    // 1. Animation de destruction (le hublot se tord et disparaît)
    setTimeout(() => {
      const app = document.body
      app.style.transition = "all 2s ease-out"
      app.style.transform = "scale(0.8) skewX(20deg) skewY(5deg)" // Plus de distorsion
      app.style.filter = "blur(15px) contrast(200%) grayscale(100%)" // Effet glitch visuel
      app.style.opacity = "0"

      // 2. Apparition du message FATAL ERROR avec effet
      setTimeout(() => {
        // RESET COMPLET DU BODY
        app.style = "" // On nettoie tous les styles inline précédents
        app.style.backgroundColor = "#000" // Fond noir immédiat
        app.style.margin = "0"
        app.style.overflow = "hidden"

        // INJECTION DU NOUVEAU CONTENU AVEC STYLE CSS INTÉGRÉ
        document.body.innerHTML = `
          <style>
            /* Effet d'allumage CRT (Ligne qui s'ouvre) */
            @keyframes crt-turn-on {
              0% { transform: scale(1, 0.005); opacity: 0; filter: brightness(3); }
              40% { transform: scale(1, 0.005); opacity: 1; }
              70% { transform: scale(1, 1.2); }
              100% { transform: scale(1, 1); opacity: 1; filter: brightness(1); }
            }

            /* Scintillement dramatique du texte rouge */
            @keyframes text-flicker {
              0% { opacity: 0; }
              10% { opacity: 0.5; text-shadow: none; }
              20% { opacity: 0; }
              30% { opacity: 1; text-shadow: 0 0 20px red; }
              40% { opacity: 0.3; }
              50% { opacity: 1; text-shadow: 0 0 10px red; }
              100% { opacity: 1; text-shadow: 0 0 15px red, 0 0 30px darkred; }
            }

            /* Clignotement du curseur */
            @keyframes blink { 50% { opacity: 0; } }

            .fatal-container {
              height: 100vh;
              width: 100vw;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              background-color: black;
              color: white;
              font-family: 'Courier New', monospace;
              text-align: center;
              
              /* L'animation d'ouverture */
              animation: crt-turn-on 0.4s ease-out forwards;
              transform-origin: center;
            }

            h1 {
              font-size: 5rem;
              color: #ff3333;
              margin: 0;
              text-transform: uppercase;
              letter-spacing: 5px;
              opacity: 0; /* Caché au début */
              animation: text-flicker 1.5s 0.5s forwards; /* Démarre après 0.5s */
            }

            .sub-text {
              margin-top: 20px;
              font-size: 1.2rem;
              color: #cccccc;
              opacity: 0;
              animation: text-flicker 1s 1.5s forwards; /* Démarre après le titre */
            }
            
            .cursor {
              display: inline-block;
              width: 10px;
              height: 18px;
              background: #ff3333;
              animation: blink 1s infinite;
              vertical-align: middle;
            }
          </style>

          <div class="fatal-container">
            <h1>SYSTEM HALTED</h1>
            <div class="sub-text">
              > FATAL ERROR: ROOT DELETED.<br>
              > PLEASE REBOOT (F5)<span class="cursor"></span>
            </div>
          </div>
        `
      }, 2000)
    }, 1500)
  }
  else {
    feedbackMessage.value = `${t('TheHublot.cmd_not_found')} '${cmd}'`
  }

  userCommand.value = ''
}

const handleHistoryNav = (direction) => {
  if (!commandHistory.value.length) return
  if (direction === 'up') { if (historyIndex.value < commandHistory.value.length - 1) historyIndex.value++ } else { if (historyIndex.value > -1) historyIndex.value-- }
  if (historyIndex.value === -1) { userCommand.value = '' } else { userCommand.value = commandHistory.value[commandHistory.value.length - 1 - historyIndex.value] }
}

onMounted(() => { generateSnippets() })
</script>

<style scoped>
.hublot-container {
  /* Noir pur pour le style Tech */
  --bg-color: rgb(7, 1, 26);
  
  position: relative;
  
  /* DIMENSIONS : Plus large (allongé) */
  width: 95%;
  max-width: 1100px; /* <--- Augmenté */
  height: 320px;     /* <--- Ajusté pour l'aspect ratio */
  
  /* POSITION : Plus bas pour éviter la navbar */
  margin: 120px auto 0 auto; /* <--- 120px du haut */
  
  background-color: var(--bg-color);
  border-radius: 100rem; /* Bordure très arrondie style pilule */
  overflow: hidden;
  
  /* Bordure fine tech sans glow */
  border: 1px solid rgba(255,255,255,0.15);
  
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: whitesmoke;
  font-family: 'Space Mono', monospace;
}

/* --- ANIMATION CORRIGÉE --- */
.scrolling-code-layer {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
  z-index: 1;
}

.code-line {
  position: absolute;
  white-space: nowrap;
  font-family: monospace;
  font-weight: bold;
  opacity: 0.7;
  
  /* POSITION DE DÉPART : Hors champ à droite */
  left: 100%;
  
  /* ANIMATION : Va vers la gauche */
  animation: scroll-left linear infinite;
  
  /* Optionnel : ombre portée pour lisibilité */
  text-shadow: 1px 1px 2px black;
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }
  100% {
    /* On déplace de 2000px vers la gauche pour être sûr de sortir de l'écran */
    transform: translateX(-2000px); 
  }
}

/* Couleurs syntaxiques */
:deep(.keyword-blue) { color: rgb(104, 213, 230); font-weight: bold; }
:deep(.keyword-orange) { color: rgb(255, 175, 83); font-weight: bold; }
:deep(.keyword-green) { color: rgb(140, 237, 140); font-weight: bold; }
:deep(.keyword-red) { color: rgb(243, 95, 95); font-weight: bold; }

.blur-overlay {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 2;
}

.hublot-content {
  position: relative;
  z-index: 10;
  text-align: center;
  width: 80%;
}

.title-display {
  font-size: 2.8rem; /* Un peu plus grand */
  margin: 0;
  cursor: pointer;
  text-shadow: 0 0 10px black;
  transition: transform 0.2s, color 0.3s;
  text-decoration: underline;
}

/* HOVER VIOLET */
.title-display:hover { 
  transform: scale(1.02); 
  color: #a855f7; 
}

.command-interface {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.last-command-display {
  font-family: monospace;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 5px;
  align-self: flex-start;
  padding-left: 25px;
}

.cmd-history { color: #fff; font-weight: bold; }

.input-line {
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 1.5rem;
}

.prompt {
  padding:15px;
  color: #fff;
  font-weight: bold;
  animation: blink 1s infinite;
  flex-shrink: 0; 
  line-height: 1; 
}

.cmd-input {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  font-family: monospace;
  flex: 1; /* Prend l'espace restant sans pousser le voisin */
  outline: none;
  border-bottom: 2px solid transparent;  
  padding-bottom: 5px; 
  line-height: 1.2;
}

.cmd-input:focus { 
  border-bottom: 2px solid #fff; 
}

.subtitle {
  margin-top: 15px;
  font-size: 1rem;
  color: #ddd;
  height: 20px;
}
.feedback-active { color: rgb(140, 237, 140); }

@keyframes blink { 50% { opacity: 0; } }

/* Responsive Mobile : On adapte le hublot */
@media (max-width: 768px) {
  .hublot-container {
    width: 95%;
    height: 250px;
    margin-top: 80px; /* Moins d'espace en haut sur mobile */
    border-radius: 50px;
  }
  
  .title-display {
    font-size: 1.8rem;
  }
  
  .input-line, .cmd-input {
    font-size: 1.2rem;
  }
}
</style>